import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import SearchBar from '../components/SearchBar';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { searchProperties } from '../lib/api';
import { MapPin, ArrowRight, Loader2 } from 'lucide-react';

const CITY_DATA = {
  'cape-coral': {
    name: 'Cape Coral',
    description: 'Known as the "Waterfront Wonderland," Cape Coral offers over 400 miles of canals, making it perfect for boating enthusiasts and waterfront vacation rentals.',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1920&h=800&fit=crop'
  },
  'fort-myers': {
    name: 'Fort Myers',
    description: 'Historic charm meets modern amenities in Fort Myers. Explore beautiful beaches, the Edison & Ford Winter Estates, and a vibrant downtown scene.',
    image: 'https://images.unsplash.com/photo-1590523278191-995cbcda646b?w=1920&h=800&fit=crop'
  },
  'fort-myers-beach': {
    name: 'Fort Myers Beach',
    description: 'Sun-soaked beaches, casual waterfront dining, and stunning Gulf sunsets await you at Fort Myers Beach. Perfect for family getaways.',
    image: 'https://images.unsplash.com/photo-1726250947679-3d89963934d8?w=1920&h=800&fit=crop'
  },
  'sanibel': {
    name: 'Sanibel Island',
    description: 'World-renowned for shelling, Sanibel Island offers pristine beaches, wildlife refuges, and a laid-back island atmosphere unlike anywhere else.',
    image: 'https://images.unsplash.com/photo-1585770609371-56545692c027?w=1920&h=800&fit=crop'
  },
  'captiva': {
    name: 'Captiva Island',
    description: 'Intimate and exclusive, Captiva Island features stunning sunsets, fine dining, and a romantic escape from everyday life.',
    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1920&h=800&fit=crop'
  },
  'bonita-springs': {
    name: 'Bonita Springs',
    description: 'Bonita Springs combines natural beauty with upscale amenities. Enjoy pristine beaches, world-class golf, and easy access to Naples.',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&h=800&fit=crop'
  },
  'estero': {
    name: 'Estero',
    description: 'Estero offers the perfect blend of nature preserves, shopping at Coconut Point, and easy access to all of Southwest Florida\'s attractions.',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1920&h=800&fit=crop'
  },
  'naples': {
    name: 'Naples',
    description: 'Sophisticated Naples features pristine beaches, world-class shopping on Fifth Avenue, and some of the finest dining in Southwest Florida.',
    image: 'https://images.unsplash.com/photo-1673796751464-d4c1b3f86cf8?w=1920&h=800&fit=crop'
  },
  'marco-island': {
    name: 'Marco Island',
    description: 'The largest of Florida\'s Ten Thousand Islands, Marco Island offers stunning beaches, excellent fishing, and a true tropical paradise experience.',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&h=800&fit=crop'
  },
};

export default function CityPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  
  // citySlug comes as "sanibel-vacation-rentals", extract the city ID
  const isValidCityPage = citySlug?.endsWith('-vacation-rentals');
  const cityId = citySlug?.replace(/-vacation-rentals$/, '') || '';
  const cityInfo = CITY_DATA[cityId];
  
  // If not a valid city page URL or city not found, redirect to home
  useEffect(() => {
    if (!isValidCityPage || !cityInfo) {
      // Not a city page URL, let other routes handle it
      return;
    }
  }, [isValidCityPage, cityInfo]);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadProperties = async (cursor = null) => {
    if (!cityId) return;
    try {
      const response = await searchProperties({
        city: cityId,
        limit: 12,
        cursor
      });
      
      if (cursor) {
        setProperties(prev => [...prev, ...response.data.items]);
      } else {
        setProperties(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isValidCityPage && cityInfo) {
      loadProperties();
    }
  }, [cityId, isValidCityPage]);

  const handleLoadMore = () => {
    if (nextCursor) {
      loadProperties(nextCursor);
    }
  };
  
  // If not a valid city page, return null (let other routes handle)
  if (!isValidCityPage || !cityInfo) {
    return null;
  }

  return (
    <PublicLayout>
      <SEO
        title={`${cityInfo.name} Vacation Rentals`}
        description={`Browse vacation homes in ${cityInfo.name}, Southwest Florida. ${cityInfo.description}`}
        image={cityInfo.image}
        url={`/${citySlug}`}
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src={cityInfo.image}
            alt={cityInfo.name}
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-main relative z-10">
          <div className="flex items-center gap-2 text-white/80 mb-2">
            <MapPin className="w-5 h-5" />
            <span>Southwest Florida</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            {cityInfo.name} Vacation Rentals
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            {cityInfo.description}
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="container-main -mt-8 relative z-20">
        <SearchBar initialValues={{ city: cityId }} />
      </section>

      {/* Properties List */}
      <section className="section-padding">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Properties in {cityInfo.name}
            </h2>
            {properties.length > 0 && (
              <p className="text-slate-500">
                {hasMore ? '20+ results' : `${properties.length} results`}
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : properties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-12 text-center">
                  <Button 
                    onClick={handleLoadMore}
                    className="btn-outline"
                  >
                    Load More Properties
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                No properties found in {cityInfo.name}
              </h3>
              <p className="text-slate-500 mb-6">
                Check back soon or explore other Southwest Florida destinations.
              </p>
              <Link to="/search">
                <Button className="btn-primary">
                  Browse All Properties
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
